import React from 'react';

const PrivacyPage = () => {
  return (
    <div className="bg-[#0A0A14] text-[#F0EFF4] font-heading min-h-screen">
      <div className="max-w-3xl mx-auto px-6 py-20">
        {/* Header */}
        <h1 className="text-4xl md:text-5xl font-bold text-center mb-2">
          Privacy Policy
        </h1>
        <p className="text-sm text-[#F0EFF4]/40 text-center mb-16">
          Last updated: October 2026
        </p>

        <h2 className="text-2xl font-bold text-center mb-12 tracking-wide">
          ROOFCLAW PRIVACY POLICY
        </h2>

        {/* Section 1 */}
        <div className="border-b border-[#F0EFF4]/10 pb-8 mb-8">
          <h3 className="text-lg font-semibold mb-3">1. Who We Are</h3>
          <p className="text-[#F0EFF4]/70 leading-relaxed">
            RBP Consulting, Inc. (&ldquo;Company&rdquo;, operating as RoofClaw) provides AI-powered operations tools for roofing companies. This policy explains how we collect, use, and protect your information when you use our website, chatbot, client portal, or purchase our services.
          </p>
        </div>

        {/* Section 2 */}
        <div className="border-b border-[#F0EFF4]/10 pb-8 mb-8">
          <h3 className="text-lg font-semibold mb-3">2. Information We Collect</h3>
          <p className="text-[#F0EFF4]/70 leading-relaxed mb-3">
            We collect information you provide directly:
          </p>
          <ul className="list-disc list-inside text-[#F0EFF4]/70 leading-relaxed space-y-1 ml-2">
            <li>Name, email address, phone number, and company name when you interact with our chatbot or forms</li>
            <li>Business context and integration preferences submitted through our onboarding portal</li>
            <li>Payment information processed by Stripe (we do not store card details)</li>
            <li>Credentials submitted through our secure portal for system integrations</li>
          </ul>
          <p className="text-[#F0EFF4]/70 leading-relaxed mt-3">
            We automatically collect:
          </p>
          <ul className="list-disc list-inside text-[#F0EFF4]/70 leading-relaxed space-y-1 ml-2">
            <li>Browser type, device information, and IP address</li>
            <li>Pages visited, time on site, and referral source (via analytics and Meta Pixel)</li>
            <li>Error reports via Sentry for service reliability</li>
          </ul>
        </div>

        {/* Section 3 */}
        <div className="border-b border-[#F0EFF4]/10 pb-8 mb-8">
          <h3 className="text-lg font-semibold mb-3">3. How We Use Your Information</h3>
          <ul className="list-disc list-inside text-[#F0EFF4]/70 leading-relaxed space-y-1 ml-2">
            <li>To evaluate your fit for RoofClaw services (including automated chatbot scoring)</li>
            <li>To configure and deliver your AI assistant deployment</li>
            <li>To process payments and manage your account</li>
            <li>To provide customer support and track onboarding progress</li>
            <li>To improve our website and services</li>
            <li>To send transactional communications (order confirmations, onboarding updates)</li>
          </ul>
        </div>

        {/* Section 4 */}
        <div className="border-b border-[#F0EFF4]/10 pb-8 mb-8">
          <h3 className="text-lg font-semibold mb-3">4. Data Sovereignty</h3>
          <p className="text-[#F0EFF4]/70 leading-relaxed">
            RoofClaw is built on the principle of data sovereignty. Once your AI assistant is deployed on your dedicated hardware, your business data, credentials, and agent configuration reside on your physical device. We do not retain access to your systems post-delivery. Your data stays yours.
          </p>
        </div>

        {/* Section 5 */}
        <div className="border-b border-[#F0EFF4]/10 pb-8 mb-8">
          <h3 className="text-lg font-semibold mb-3">5. Third-Party Services</h3>
          <p className="text-[#F0EFF4]/70 leading-relaxed mb-3">
            We use the following third-party services that may process your data:
          </p>
          <ul className="list-disc list-inside text-[#F0EFF4]/70 leading-relaxed space-y-1 ml-2">
            <li><strong>Stripe</strong> &mdash; payment processing</li>
            <li><strong>HubSpot</strong> &mdash; CRM and scheduling</li>
            <li><strong>Supabase</strong> &mdash; secure database hosting</li>
            <li><strong>Sentry</strong> &mdash; error tracking and reliability monitoring</li>
            <li><strong>Meta Pixel</strong> &mdash; advertising attribution (you may opt out via browser settings)</li>
            <li><strong>K&amp;K Advertising (Voluum)</strong> &mdash; advertising attribution. Their script runs on our public pages and receives the page address, title and referrer, your IP address and browser details, and sets cookies and local storage. If you arrived from one of our ads and then book a call or buy, your email and ad click ID are also shared with them and with Meta to measure the ad. It does not run on client portal, admin or checkout pages</li>
            <li><strong>Resend</strong> &mdash; transactional email delivery</li>
          </ul>
          <p className="text-[#F0EFF4]/70 leading-relaxed mt-3">
            Each service operates under its own privacy policy. We do not sell your data to any third party.
          </p>
        </div>

        {/* Section 6 */}
        <div className="border-b border-[#F0EFF4]/10 pb-8 mb-8">
          <h3 className="text-lg font-semibold mb-3">6. Cookies and Tracking</h3>
          <p className="text-[#F0EFF4]/70 leading-relaxed">
            We use cookies and similar technologies for site functionality, analytics, and advertising attribution. You can control cookie preferences through your browser settings. Disabling cookies may affect certain site features.
          </p>
        </div>

        {/* Section 7 */}
        <div className="border-b border-[#F0EFF4]/10 pb-8 mb-8">
          <h3 className="text-lg font-semibold mb-3">7. Data Retention</h3>
          <p className="text-[#F0EFF4]/70 leading-relaxed">
            We retain chatbot conversation data and lead information for up to 12 months to facilitate onboarding and improve our services. Portal credentials submitted via secure links are auto-expiring. You may request deletion of your data at any time by contacting us.
          </p>
        </div>

        {/* Section 8 */}
        <div className="border-b border-[#F0EFF4]/10 pb-8 mb-8">
          <h3 className="text-lg font-semibold mb-3">8. Your Rights</h3>
          <p className="text-[#F0EFF4]/70 leading-relaxed mb-3">
            Depending on your jurisdiction, you may have the right to:
          </p>
          <ul className="list-disc list-inside text-[#F0EFF4]/70 leading-relaxed space-y-1 ml-2">
            <li>Access the personal data we hold about you</li>
            <li>Request correction of inaccurate data</li>
            <li>Request deletion of your data</li>
            <li>Opt out of marketing communications</li>
            <li>Opt out of automated decision-making</li>
          </ul>
          <p className="text-[#F0EFF4]/70 leading-relaxed mt-3">
            To exercise any of these rights, contact us at the address below.
          </p>
        </div>

        {/* Section 9 */}
        <div className="border-b border-[#F0EFF4]/10 pb-8 mb-8">
          <h3 className="text-lg font-semibold mb-3">9. Security</h3>
          <p className="text-[#F0EFF4]/70 leading-relaxed">
            We implement industry-standard security measures including encryption in transit, secure credential handling, and access controls. Our portal uses token-based authentication with automatic expiry. Credential submissions use self-destructing encrypted links.
          </p>
        </div>

        {/* Section 10 */}
        <div className="pb-8 mb-12">
          <h3 className="text-lg font-semibold mb-3">10. Changes to This Policy</h3>
          <p className="text-[#F0EFF4]/70 leading-relaxed">
            We may update this policy from time to time. Changes will be posted on this page with an updated &ldquo;Last updated&rdquo; date. Continued use of our services after changes constitutes acceptance.
          </p>
        </div>

        {/* Footer note */}
        <p className="text-xs text-[#F0EFF4]/30 text-center">
          Questions about your privacy? Contact us at{' '}
          <a href="mailto:support@roofclaw.com" className="hover:underline">
            support@roofclaw.com
          </a>
        </p>
      </div>
    </div>
  );
};

export default PrivacyPage;
